import type { WorkspaceAsset } from '../types'

export const STANDARD_PRODUCT_ASSETS: WorkspaceAsset[] = [
  {
    id: 'product-agent',
    sectionKey: 'product',
    type: 'external_link',
    title: 'A dedicated AI career agent for every {noun}',
    description: 'Prentus uses each {noun}’s program, goals, experience, materials, and job-search activity to give useful next steps at any hour.',
    url: 'https://prentus.com/features/ai-career-advisor',
    source: 'standard',
    metadata: {
      category: 'Always-on {noun} support',
      image: '/images/product/ai-agent.webp',
      features: ['24/7 coaching', 'Personalized career plans', 'Proactive next steps'],
    },
  },
  {
    id: 'product-interviews',
    sectionKey: 'product',
    type: 'external_link',
    title: 'Unlimited AI mock interviews',
    description: '{Nouns} practice realistic conversations, receive immediate feedback, and arrive at live coaching sessions ready for higher-value help.',
    url: 'https://prentus.com/features/ai-mock-interviews',
    source: 'standard',
    metadata: {
      category: 'Career preparation',
      image: '/images/product/mock-interviews.webp',
      features: ['Voice practice', 'Job-specific questions', 'Instant feedback'],
    },
  },
  {
    id: 'product-job-search',
    sectionKey: 'product',
    type: 'external_link',
    title: 'Job search tools in one {noun} workflow',
    description: 'Job aggregation, application tracking, resume tools, outreach support, and follow-up live together so {nouns} always know what to do next.',
    url: 'https://prentus.com/features/job-tracker',
    source: 'standard',
    metadata: {
      category: 'Job search',
      image: '/images/product/job-search-tools.webp',
      features: ['Job aggregation', 'Application tracker', 'Resume and outreach tools'],
    },
  },
  {
    id: 'product-community',
    sectionKey: 'product',
    type: 'external_link',
    title: 'A career community {nouns} return to',
    description: 'Cohort spaces, events, career points, direct messaging, and peer support keep career progress visible and social.',
    url: 'https://prentus.com/features/community',
    source: 'standard',
    metadata: {
      category: '{Noun} engagement',
      image: '/images/product/community.webp',
      features: ['Cohort spaces', 'Events and messaging', 'Career points'],
    },
  },
  {
    id: 'product-advisor',
    sectionKey: 'product',
    type: 'external_link',
    title: 'An advisor command center',
    description: 'Career teams see who is active, who needs help, what materials require review, and where intervention can change an outcome.',
    url: 'https://prentus.com/features/advisor-suite',
    source: 'standard',
    metadata: {
      category: 'Team capacity',
      image: '/images/product/advisor-roster.webp',
      features: ['Advisor caseloads', 'Review queues', 'Scheduling and campaigns'],
    },
  },
  {
    id: 'product-outcomes',
    sectionKey: 'product',
    type: 'external_link',
    title: 'Verified outcome tracking',
    description: 'Prentus combines {noun} activity, self-reported outcomes, and verified LinkedIn scans so leadership can see progress without spreadsheet chasing.',
    url: 'https://prentus.com/features/outcome-tracking',
    source: 'standard',
    metadata: {
      category: 'Leadership visibility',
      image: '/images/product/outcome-tracking.webp',
      features: ['LinkedIn scans', 'First-destination surveys', 'Program and cohort reporting'],
    },
  },
  {
    id: 'product-employers',
    sectionKey: 'product',
    type: 'external_link',
    title: 'Employer relationships tied to {noun} action',
    description: 'Manage employer partners, share matched talent, bring in relevant roles, and see whether {nouns} actually apply.',
    url: 'https://prentus.com/features/employer-partnerships',
    source: 'standard',
    metadata: {
      category: 'Employer engagement',
      image: '/images/product/employer-partnerships.webp',
      features: ['Employer CRM', 'Talent links', 'Matched job distribution'],
    },
  },
  {
    id: 'product-student-tour',
    sectionKey: 'product',
    type: 'iframe',
    title: 'See the Prentus {noun} experience',
    description: 'A guided look at how Prentus moves {nouns} from their first career plan through preparation, applications, and employment.',
    url: 'https://www.tella.tv/video/vid_cmrmp090a00450akf7eny9x7m/embed?b=0&title=0&muted=1&wt=0&o=1',
    source: 'standard',
    metadata: { category: '{Noun} Experience' },
  },
  {
    id: 'product-platform-tour',
    sectionKey: 'product',
    type: 'iframe',
    title: 'See the Prentus admin experience',
    description: 'A short walkthrough of how career teams manage {nouns}, engagement, and outcomes in one place.',
    url: 'https://www.tella.tv/video/cm35xb26a000p03mp8jda9ukd/embed',
    source: 'standard',
    metadata: { category: 'Admin Experience' },
  },
]

export const STANDARD_CASE_STUDY_ASSETS: WorkspaceAsset[] = [
  {
    id: 'case-quickstart',
    sectionKey: 'case-studies',
    type: 'external_link',
    title: 'Turned career support into measurable financial value',
    description: 'QuickStart replaced spreadsheet-based career services with one platform that made student engagement and job-search progress visible.',
    url: 'https://prentus.com/case-studies/quickstart',
    source: 'standard',
    metadata: {
      customer: 'QuickStart',
      stat: '$500K saved',
      logo: '/images/logos/quickstart.svg',
      outcomes: ['17% fewer refund requests', '60%+ career services engagement', 'No additional career coach needed'],
      sourceUrl: 'https://prentus.com/case-studies/quickstart',
    },
  },
  {
    id: 'case-fullstack',
    sectionKey: 'case-studies',
    type: 'external_link',
    title: 'Consolidated five tools into one system of record',
    description: 'Fullstack Academy gave advisors one view of every student while supporting 3,800 students with a leaner team.',
    url: 'https://prentus.com/case-studies/fullstack-academy',
    source: 'standard',
    metadata: {
      customer: 'Fullstack Academy',
      stat: '$617K annual savings',
      logo: '/images/logos/fullstack-academy.png',
      outcomes: ['3x operational efficiency', '50% higher engagement', '3,800 students supported'],
      sourceUrl: 'https://prentus.com/case-studies/fullstack-academy',
    },
  },
  {
    id: 'case-climb-hire',
    sectionKey: 'case-studies',
    type: 'external_link',
    title: 'Automated outcome tracking without losing the personal touch',
    description: 'Climb Hire moved manual LinkedIn checks and alumni activity into a continuous, measurable workflow.',
    url: 'https://prentus.com/case-studies/climb-hire',
    source: 'standard',
    metadata: {
      customer: 'Climb Hire',
      stat: '16 to 20 hours saved weekly',
      logo: '/images/logos/climb-hire.svg',
      outcomes: ['50% less tracking time', '2x alumni engagement', 'One digital home for alumni'],
      sourceUrl: 'https://prentus.com/case-studies/climb-hire',
    },
  },
  {
    id: 'case-student-love',
    sectionKey: 'case-studies',
    type: 'iframe',
    title: 'What {nouns} say about Prentus',
    description: 'A live collection of {noun} feedback, including written and video testimonials.',
    url: 'https://embed-v2.testimonial.to/w/prentus?animated=on&theme=light&shadowColor=ffffff&speed=1&hideDate=on&hideSource&tag=member&showHeartAnim',
    source: 'standard',
    metadata: { customer: '{Nouns} using Prentus' },
  },
]

/**
 * The only library a viewer without a deal sees: pre-demo customer
 * workspaces (worker/data.ts) and the partner area's Product and Case
 * Studies sections both read this list, so they always match.
 */
export const STANDARD_PRE_DEAL_ASSETS: WorkspaceAsset[] = [...STANDARD_PRODUCT_ASSETS, ...STANDARD_CASE_STUDY_ASSETS]

/**
 * Product launch updates shown on the Customer Success "What's New" tab in
 * every workspace. Publish a launch once here and every client portal shows
 * it. Keep entries newest first.
 */
export const STANDARD_WHATS_NEW_ASSETS: WorkspaceAsset[] = [
  {
    id: 'whats-new-prentus-3-0',
    sectionKey: 'whats-new',
    type: 'external_link',
    title: 'Prentus 3.0 is here',
    description: 'A faster platform with a redesigned {noun} experience, smarter AI career guidance, and a new advisor command center.',
    // TODO: point at the live 3.0 launch page once Ashley confirms its URL.
    url: 'https://prentus.com',
    source: 'standard',
    metadata: { category: 'Platform launch', image: '/images/product/platform-overview.webp' },
  },
  {
    id: 'whats-new-automated-workflows',
    sectionKey: 'whats-new',
    type: 'external_link',
    title: 'New: Automated Workflows',
    description: 'Trigger emails and texts from {noun} behavior, like a day-5 nudge for invitees who have not activated.',
    url: 'https://docs.prentus.com/en/articles/13680114-introduction-to-automated-workflows',
    source: 'standard',
    metadata: { category: 'Feature update', image: '/images/product/advisor-roster.webp' },
  },
]

/**
 * Standard Prentus resources shown on the Customer Success "Resources" tab in
 * every workspace. Client-specific files continue to live as workspace asset
 * rows with sectionKey 'resources' or 'collateral'.
 */
export const STANDARD_RESOURCE_ASSETS: WorkspaceAsset[] = [
  {
    id: 'resource-help-center',
    sectionKey: 'resources',
    type: 'external_link',
    title: 'Help Center',
    description: 'Setup guides, feature documentation, and answers for admins and advisors.',
    url: 'https://docs.prentus.com/en/',
    source: 'standard',
    metadata: { category: 'Documentation', image: '/images/product/platform-overview.webp' },
  },
  {
    id: 'resource-jobseeker-docs',
    sectionKey: 'resources',
    type: 'external_link',
    title: '{Noun} Documentation',
    description: 'Everything {nouns} need to get started and make the most of the platform.',
    url: 'https://docs.prentus.com/en/collections/10837572-jobseeker-experience',
    source: 'standard',
    metadata: { category: 'Documentation', image: '/images/product/job-search-tools.webp' },
  },
  {
    id: 'resource-jobseeker-demos',
    sectionKey: 'resources',
    type: 'video',
    title: '{Noun} Demos',
    description: 'Short walkthrough videos of the {noun} experience your team can share.',
    url: 'https://www.tella.tv/playlist/pl_cmqs4it1703rj04l74dzy0lbg/public',
    source: 'standard',
    metadata: { category: 'Video demos', image: '/images/product/mock-interviews.webp' },
  },
]

/**
 * Approved resources that should be available in every workspace. These stay
 * centrally referenced so content fixes propagate without copying rows into
 * every customer workspace.
 */
export const STANDARD_LIBRARY_ASSETS: WorkspaceAsset[] = [
  ...STANDARD_PRODUCT_ASSETS,
  ...STANDARD_CASE_STUDY_ASSETS,
  ...STANDARD_WHATS_NEW_ASSETS,
  ...STANDARD_RESOURCE_ASSETS,
]

export function withStandardLibraryAssets(workspaceAssets: WorkspaceAsset[]): WorkspaceAsset[] {
  const assetsById = new Map(STANDARD_LIBRARY_ASSETS.map((asset) => [asset.id, asset]))
  for (const asset of workspaceAssets) assetsById.set(asset.id, asset)
  return Array.from(assetsById.values())
}
